/* =========================================================================
   HandFab bespoke PDP controller  (vanilla, no dependencies)
   ------------------------------------------------------------------------
   Registers a <handfab-pdp> custom element. One instance per product form.
   Responsibilities:
     • image gallery thumbnail switching
     • compose the correct Shopify variant from two product options
       (Steel = price-bearing option, Engraving = Yes/No option)
     • keep the hidden [name="id"] input + price + steel note in sync
     • reveal/hide the engraving text field and its line-item property
     • quantity stepper
   The <form> itself is Dawn's native product form, so Add-to-Cart, sold-out
   handling, and checkout all use standard Shopify behaviour.

   =========================================================================
   CHANGES IN THIS VERSION
   -------------------------------------------------------------------------
   0. THE CART DRAWER NOW OPENS ON ADD TO CART.
      Add to Cart was redirecting to /cart. Dawn's drawer only opens if
      something cancels the native submit and posts to /cart/add.js in the
      background; the section had a bare form with nothing doing that, so the
      browser submitted normally and Shopify answered with a redirect.

      The section is now wrapped in <product-form>, which is the correct fix
      IF assets/product-form.js exists and defines that element. On this theme
      it evidently does not, or does not load, because the wrapper alone did
      not change the behaviour.

      SO THIS FILE NOW HANDLES THE SUBMIT ITSELF, and does it defensively:

        - If <product-form> HAS upgraded, this handler stands down completely
          and lets Dawn do the work. Both firing would add the item twice.
        - If there is no cart-drawer or cart-notification element on the page,
          or it has no renderContents method, this handler stands down and the
          form submits natively. That is a cart-type of Page, where a redirect
          to /cart is the correct behaviour.
        - Only when the drawer exists AND nothing else is handling the form
          does it intercept.

      It posts the same way Dawn does: FormData to routes.cart_add_url with
      Accept application/javascript and X-Requested-With, requesting the
      drawer's own sections back, then hands the response to
      renderContents(), which is what fills and opens the drawer.

      IT CANNOT HANG. Everything after the interception is inside a try/catch
      that releases the button and falls back to a native submit. That is the
      exact failure mode this whole area produced once already: Dawn's own
      handler threw after setting the loading state, so the button spun
      forever and no request was ever sent. A handler that can hang is worse
      than no handler at all, because a redirect to /cart still completes the
      sale.

      TO REMOVE THIS LATER: if product-form.js is ever restored, this handler
      switches itself off automatically. Nothing needs deleting.

   1. THE ADD TO CART LABEL NO LONGER CONTAINS THE PRICE.
      It was:
        lbl.textContent = variant.available ? ('Add to Cart – ' + money) : 'Sold out';
      The price made the label too long for a narrow phone, where it had to
      truncate with an ellipsis and the price was the part that got cut. The
      section template now shows the price on its own line above the quantity
      row, so the button says one thing.

      This line was also why the template change alone did nothing: the Liquid
      rendered "Add to Cart", then this script overwrote it on connect.

      DELETE THE "ADD TO CART LABEL GUARD" SCRIPT BLOCK from
      handfab-pdp.liquid once this file is live. It was a MutationObserver
      patching this behaviour from outside and it is dead weight now.

   2. ALL PRICE ELEMENTS UPDATE, NOT JUST THE FIRST.
      priceLabel was a single querySelector, so only one [data-hf-price] node
      was ever updated. The template carries two: the visible price row and a
      hidden .hf-vh fallback. It is querySelectorAll now.

   3. THE RENDERED VARIANT IS RESPECTED ON LOAD.
      The steel selection was initialised from the first AVAILABLE variant,
      while Liquid renders selected_or_first_available_variant. On a
      ?variant= deep link, or any product whose first variant is out of
      stock, the two disagreed: the page rendered one steel as selected and
      the script immediately resolved to another.
      It now reads the hidden [name="id"] input that Liquid has already set,
      and only falls back to first-available if that fails.

      ENGRAVING STILL DEFAULTS OFF, deliberately, as before. It is a paid
      opt-in, so inheriting "Yes" from a deep link would add £15 silently.

   4. AN UNRESOLVABLE COMBINATION NO LONGER FAILS SILENTLY.
      If findVariant() returned nothing, the whole block was skipped: the
      hidden id kept its previous value and the button stayed enabled, so a
      shopper could add the wrong variant. There is an else branch now that
      disables the button and labels it Unavailable.

   -------------------------------------------------------------------------
   ############################################################
   ## ONE BEHAVIOUR LEFT ALONE, WORTH A DECISION.             ##
   ## The price is multiplied by quantity:                    ##
   ##   formatMoney(variant.price * qty, ...)                 ##
   ##                                                         ##
   ## That made sense when the figure lived inside the Add to ##
   ## Cart button, where it read as a running total. On its   ##
   ## own line at 20px, directly under the product title, it  ##
   ## reads as the item's price instead, so setting quantity  ##
   ## to 2 makes the knife look like it costs twice as much.  ##
   ##                                                         ##
   ## Left as-is because it changes what shoppers see and     ##
   ## that is not a silent edit to make. To show unit price,  ##
   ## drop "* qty" from the line marked below. To keep it as  ##
   ## a running total, consider labelling it.                 ##
   ############################################################
   ========================================================================= */
(function () {
  if (customElements.get('handfab-pdp')) return;

  // Minimal money formatter driven by shop.money_format (e.g. "£{{amount}}")
  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var noDec = Math.round(cents / 100).toString();
    return (format || '£{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/g, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, value.replace('.', ','));
  }

  class HandfabPDP extends HTMLElement {
    connectedCallback() {
      this.form        = this.querySelector('form[data-hf-form]');
      this.idInput     = this.querySelector('[name="id"]');
      /* ALL price nodes, not just the first. The template carries the visible
         price row and a hidden .hf-vh fallback. */
      this.priceLabels = this.querySelectorAll('[data-hf-price]');
      this.steelNote   = this.querySelector('[data-hf-steelnote]');
      this.mainImg     = this.querySelector('[data-hf-main] img');
      this.engField    = this.querySelector('[data-hf-engfield]');
      this.engInput    = this.querySelector('[data-hf-engtext]');
      this.cta         = this.querySelector('[data-hf-cta]');
      this.pickLabel   = this.querySelector('[data-hf-pick]');
      this.moneyFormat = this.getAttribute('data-money-format') || '£{{amount}}';

      try { this.variants = JSON.parse(this.querySelector('[data-hf-variants]').textContent); }
      catch (e) { this.variants = []; }

      // Option positions: which option index carries steel / engraving.
      this.steelPos   = parseInt(this.getAttribute('data-steel-pos'), 10);   // 1-based, or NaN
      this.engravePos = parseInt(this.getAttribute('data-engrave-pos'), 10); // 1-based, or NaN

      /* ---- Current selection state ------------------------------------
         Start from the variant Liquid actually rendered, which is
         selected_or_first_available_variant and already sits in the hidden
         id input. Falls back to the first available variant, then to the
         first variant at all.
         This matters on ?variant= deep links and on any product whose first
         variant is out of stock: the old first-available lookup could
         silently disagree with what the page had rendered as selected. */
      var rendered = this.renderedVariant();
      this.steelValue = (this.steelPos && rendered) ? rendered['option' + this.steelPos] : null;

      /* Engraving always defaults OFF. It is a paid opt-in, so inheriting
         "Yes" from a deep link would add £15 without the shopper choosing
         it. Deliberate, unchanged. */
      this.engraveOn = false;

      this.bindGallery();
      this.bindSteel();
      this.bindEngraving();
      this.bindQty();
      this.bindSubmit();
      this.update();
    }

    /* ---- which variant did Liquid render? ------------------------------- */
    renderedVariant() {
      var self = this;
      var id = this.idInput ? parseInt(this.idInput.value, 10) : NaN;
      var match = null;
      if (!isNaN(id)) {
        match = this.variants.find(function (v) { return v.id === id; });
      }
      if (!match) {
        match = this.variants.find(function (v) { return v.available; });
      }
      return match || this.variants[0] || null;
    }

    /* ---- add to cart ----------------------------------------------------
       See item 0 at the top of this file. This exists because Dawn's
       product-form.js is not upgrading <product-form> on this theme. It stands
       down the moment anything else is handling the form. */
    cartElement() {
      return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    dawnIsHandlingThis() {
      /* If the <product-form> wrapper has upgraded, Dawn has already bound its
         own submit handler. preventDefault in one listener does not stop the
         other, so without this check an add would fire twice. */
      return !!(this.form
        && this.form.closest('product-form')
        && window.customElements
        && window.customElements.get('product-form'));
    }

    bindSubmit() {
      var self = this;
      if (!this.form) return;

      this.form.addEventListener('submit', function (evt) {
        if (self.dawnIsHandlingThis()) return;

        var cart = self.cartElement();
        /* No drawer, or a drawer that cannot render: let the form submit
           natively. On a cart-type of Page that redirect IS the right
           behaviour, so this must not be broken here. */
        if (!cart || typeof cart.renderContents !== 'function') return;

        /* EVERYTHING BELOW IS WRAPPED. The failure this replaced was Dawn's
           own handler throwing after it had already set the loading state,
           which left the button spinning forever with no request sent. A
           handler that can hang is worse than no handler at all, so if
           anything in here throws, the button is released and the form is
           submitted the old way, which at least completes the add. */
        try {
          evt.preventDefault();
          if (self.cta && self.cta.getAttribute('aria-disabled') === 'true') return;

          self.setLoading(true);

          var formData = new FormData(self.form);
          if (typeof cart.getSectionsToRender === 'function') {
            formData.append('sections', cart.getSectionsToRender().map(function (s) { return s.id; }));
            formData.append('sections_url', window.location.pathname);
          }
          if (typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.activeElement);
          }

          var url = (window.routes && window.routes.cart_add_url) || '/cart/add';

          fetch(url, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData
          })
            .then(function (response) { return response.json(); })
            .then(function (parsed) {
              /* Shopify returns a description on failure, for example a
                 variant that sold out between page load and submit. */
              if (parsed.status) {
                self.showError(parsed.description || parsed.message || 'This item could not be added.');
                return;
              }
              self.showError('');
              if (cart.classList) cart.classList.remove('is-empty');
              cart.renderContents(parsed);
            })
            .catch(function () {
              self.showError('Something went wrong adding this to your cart. Please try again.');
            })
            .finally(function () {
              self.setLoading(false);
            });
        } catch (err) {
          /* Release the button, then fall back to a native submit. */
          self.setLoading(false);
          self.showError('');
          if (window.console && console.warn) {
            console.warn('HandFab PDP: AJAX add failed, falling back to a normal submit.', err);
          }
          self.form.submit();
        }
      });
    }

    setLoading(on) {
      if (!this.cta) return;
      if (on) {
        this.cta.setAttribute('aria-disabled', 'true');
        this.cta.classList.add('loading');
      } else {
        this.cta.removeAttribute('aria-disabled');
        this.cta.classList.remove('loading');
      }
    }

    showError(message) {
      var wrapper = this.querySelector('.product-form__error-message-wrapper');
      var target = this.querySelector('.product-form__error-message');
      if (!wrapper || !target) return;
      target.textContent = message || '';
      if (message) { wrapper.removeAttribute('hidden'); }
      else { wrapper.setAttribute('hidden', ''); }
    }

    /* ---- gallery -------------------------------------------------------- */
    bindGallery() {
      var self = this;
      var thumbs = this.querySelectorAll('[data-hf-thumb]');
      var swipe = this.querySelector('[data-hf-swipe]');
      var slides = this.querySelectorAll('[data-hf-slide]');
      var dots = this.querySelectorAll('[data-hf-dot]');

      function setActive(index) {
        thumbs.forEach(function (b) {
          b.setAttribute('aria-current', b.getAttribute('data-index') === String(index) ? 'true' : 'false');
        });
        dots.forEach(function (d) {
          if (d.getAttribute('data-index') === String(index)) d.classList.add('on');
          else d.classList.remove('on');
        });
      }

      // Thumbnail click: swap desktop main image AND scroll the mobile swipe track
      thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var index = btn.getAttribute('data-index');
          if (self.mainImg) {
            self.mainImg.src = btn.getAttribute('data-src');
            self.mainImg.alt = btn.getAttribute('data-alt') || '';
          }
          // move the swipe track to the matching slide (mobile)
          if (swipe && slides.length) {
            var target = self.querySelector('[data-hf-slide][data-index="' + index + '"]');
            if (target) swipe.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
          }
          setActive(index);
        });
      });

      // Swipe: as the track scrolls, update the active thumbnail
      if (swipe && slides.length) {
        var scrollTimer = null;
        swipe.addEventListener('scroll', function () {
          if (scrollTimer) window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(function () {
            var mid = swipe.scrollLeft + (swipe.clientWidth / 2);
            var current = 0;
            slides.forEach(function (slide) {
              if (slide.offsetLeft <= mid) current = slide.getAttribute('data-index');
            });
            setActive(current);
          }, 80);
        });
      }
    }

    /* ---- steel option --------------------------------------------------- */
    bindSteel() {
      var self = this;
      this.steelBtns = this.querySelectorAll('[data-hf-steel]');
      this.steelBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.steelValue = btn.getAttribute('data-value');
          self.update();
        });
      });
    }

    /* ---- engraving option ---------------------------------------------- */
    bindEngraving() {
      var self = this;
      this.engBtn = this.querySelector('[data-hf-engtoggle]');
      if (this.engBtn) {
        this.engBtn.addEventListener('click', function () {
          self.engraveOn = !self.engraveOn;
          self.update();
        });
      }
    }

    /* ---- quantity ------------------------------------------------------- */
    bindQty() {
      var self = this;
      this.qtyInput = this.querySelector('[data-hf-qty]');
      var dec = this.querySelector('[data-hf-qtydec]');
      var inc = this.querySelector('[data-hf-qtyinc]');
      if (dec) dec.addEventListener('click', function () { self.setQty(-1); });
      if (inc) inc.addEventListener('click', function () { self.setQty(1); });
    }
    setQty(delta) {
      if (!this.qtyInput) return;
      var v = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + delta);
      this.qtyInput.value = v;
      this.update();
    }

    /* ---- resolve the matching variant & sync UI ------------------------ */
    findVariant() {
      var self = this;
      var engValue = this.engraveOn ? 'Yes' : 'No';
      return this.variants.find(function (v) {
        var steelOk = !self.steelPos || v['option' + self.steelPos] === self.steelValue;
        var engOk   = !self.engravePos || v['option' + self.engravePos] === engValue;
        return steelOk && engOk;
      });
    }

    setLabel(text) {
      if (!this.cta) return;
      var lbl = this.cta.querySelector('[data-hf-ctatext]');
      if (lbl) lbl.textContent = text;
    }

    update() {
      var self = this;
      // steel button pressed states + notes
      this.steelBtns.forEach(function (btn) {
        var on = btn.getAttribute('data-value') === self.steelValue;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on && self.steelNote) self.steelNote.textContent = btn.getAttribute('data-note') || '';
      });

      // update the "SELECT STEEL" pick label to the chosen value
      if (this.pickLabel && this.steelValue) this.pickLabel.textContent = this.steelValue;

      // engraving states
      if (this.engBtn) this.engBtn.setAttribute('aria-pressed', this.engraveOn ? 'true' : 'false');
      if (this.engField) this.engField.classList.toggle('is-open', this.engraveOn);
      if (this.engInput) this.engInput.disabled = !this.engraveOn; // disabled inputs aren't submitted

      // resolve variant → hidden id + price + button state
      var variant = this.findVariant();

      if (variant) {
        if (this.idInput) this.idInput.value = variant.id;
        var qty = this.qtyInput ? (parseInt(this.qtyInput.value, 10) || 1) : 1;

        /* PRICE. Drop "* qty" here to show unit price instead of a running
           total. See the note at the top of this file. */
        var money = formatMoney(variant.price * qty, this.moneyFormat);

        this.priceLabels.forEach(function (el) { el.textContent = money; });

        if (this.cta) {
          this.cta.disabled = !variant.available;
          /* LABEL ONLY. The price used to be concatenated in here and it is
             the reason the button truncated on mobile. It lives on its own
             line in the template now. */
          this.setLabel(variant.available ? 'Add to Cart' : 'Sold out');
        }
      } else {
        /* No variant matches this combination. Previously this branch did not
           exist, so the hidden id kept its previous value and the button
           stayed enabled: a shopper could add the wrong variant to the cart. */
        if (this.cta) {
          this.cta.disabled = true;
          this.setLabel('Unavailable');
        }
      }
    }
  }

  customElements.define('handfab-pdp', HandfabPDP);

  // keep price in step with the qty field
  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches('[data-hf-qty]')) {
      var host = e.target.closest('handfab-pdp');
      if (host && host.update) host.update();
    }
  });
})();